import type { AccountInfo } from '@azure/msal-browser';
import { InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../config/msalConfig';  // Use the exported instance!
import { clearAllIdbCaches } from '../utils/idbCache';
import { authStore } from '../stores/auth';
import { clearServerSession } from './sessionClient';

/**
 * Authentication service for managing MSAL authentication (#469).
 *
 * MSAL is now used ONLY to (a) perform the explicit Microsoft login from
 * /login and (b) acquire tokens for direct Microsoft Graph/SharePoint calls.
 * App API calls authorize on the httpOnly session cookie instead. Every auth
 * failure path ends at /login — never at a Microsoft popup/redirect.
 */
export class AuthService {
  // Client idle timer: REMOVED (product call 2026-10-01). The server's
  // 15-day sliding session (90-day absolute cap) is the sole idle policy —
  // a second, shorter client clock only produced surprise sign-outs and a
  // warning-dialog subsystem nothing needed. Shared-browser note: a shared
  // machine now relies on explicit sign-out plus server-side expiry.

  /**
   * Get the current active account
   */
  static getActiveAccount(): AccountInfo | null {
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
      return null;
    }

    // If there's an active account set, return it
    const activeAccount = msalInstance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    // Otherwise, set and return the first account
    msalInstance.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  /**
   * Clear all local session state that must not survive a logout on a shared
   * device: the app-session store + server-side session cookie, survey
   * backups, cached customer data, and the Graph client.
   */
  private static async clearLocalSession(): Promise<void> {
    // Clear the app-session user and ask the API to delete the server-side
    // session + httpOnly cookie (JS cannot clear an httpOnly cookie itself).
    authStore.clearAuth();
    await clearServerSession();

    // Clear survey backup data from localStorage (security: prevent data leakage on shared devices)
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('survey-backup-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch { /* localStorage unavailable — non-critical */ }

    // Evict cached customer floorplans, photos, and the device catalog from
    // IndexedDB so they don't leak to the next user on a shared/returned iPad.
    await clearAllIdbCaches();

    // A portal JWT is separate from MSAL and must not survive sign-out on a
    // shared device. Dynamic import avoids the reverse AuthService import. (#424)
    try {
      const { resetPortalSession } = await import('./portalApiClient');
      resetPortalSession();
    } catch { /* non-critical */ }

  }

  /**
   * Shared local sign-out: clear everything, then land on /login. There is
   * deliberately NO Microsoft logout (logoutPopup/logoutRedirect) — the app
   * session is what ends, and re-auth always starts with an explicit click
   * on the login page (#469).
   */
  private static async signOutLocal(): Promise<void> {
    const account = this.getActiveAccount();

    await this.clearLocalSession();

    // Local MSAL cache clear is the load-bearing step: isAuthenticated() is
    // false immediately after, regardless of what navigation does next.
    if (account) {
      try {
        await msalInstance.clearCache({ account });
      } catch (err) {
        console.warn('[Auth] clearCache failed:', err);
      }
    }

    this.navigateToLogin();
  }

  /**
   * Sign out (explicit user action, e.g. a button click, or an automatic
   * auth-death path like portalApiClient's unrecoverable-renewal sign-out).
   * Clears the app session + MSAL cache locally and navigates to /login.
   * There is deliberately NO Microsoft logout (logoutPopup/logoutRedirect)
   * and no idle timer — #469; idle policy is the server's sliding session.
   */
  static async signOut(): Promise<void> {
    await this.signOutLocal();
  }

  /**
   * Get an access token silently. Only used for direct Microsoft
   * Graph/SharePoint calls now — app API calls send the session cookie.
   *
   * On an unrecoverable failure there is no popup/redirect fallback:
   * the local session is cleared and the user is sent to /login to click
   * Sign In again (#469).
   */
  static async getAccessToken(scopes: string[] = loginRequest.scopes): Promise<string> {
    const account = this.getActiveAccount();

    if (!account) {
      throw new Error('No active account found. Please sign in.');
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: scopes,
        account: account,
        forceRefresh: false,
      });
      return response.accessToken;
    } catch (error: unknown) {
      console.log('Silent token acquisition failed:', error);

      if (isUnrecoverableSilentAuthError(error)) {
        await this.signOutLocal();
        throw new Error('Authentication required - redirecting to login');
      }

      throw error;
    }
  }

  /**
   * Navigate to /login, remembering where the user was so the post-login
   * redirect (App.tsx) can return them there. The survey recovery flag lets
   * useSurvey restore in-progress survey data after the round trip.
   */
  private static navigateToLogin(): void {
    if (window.location.pathname === '/login') return;
    try {
      sessionStorage.setItem('authRedirectPath', window.location.pathname + window.location.search);
      sessionStorage.setItem('survey-auth-redirect-recovery', 'true');
    } catch { /* sessionStorage unavailable — non-critical */ }
    window.location.assign('/login');
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return this.getActiveAccount() !== null;
  }

  /**
   * Get user profile information
   */
  static getUserProfile(): AccountInfo | null {
    return this.getActiveAccount();
  }
}

export default AuthService;

/**
 * True when a silent-token failure is DEFINITIVE — renewal cannot succeed
 * without a fresh interactive sign-in, so the caller should clear the local
 * session and land on /login (#469 contract: every auth failure ends at
 * /login, never an error loop). Shared by AuthService.getAccessToken and
 * portalApiClient's id-token path so both sign out on the same set.
 *
 * Deliberately an allowlist: transient failures (offline field iPads,
 * network blips) must NOT sign anyone out, so anything not provably
 * unrecoverable rethrows. `timed_out` and `block_iframe_reload` are here
 * because they are what production actually produced (2026-09-30: the
 * silent-renewal iframe wedge) — and `timed_out` is a DIFFERENT errorCode
 * from `monitor_window_timeout`, which is how the endless-error bug slipped
 * past the original list.
 */
export function isUnrecoverableSilentAuthError(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  return error instanceof BrowserAuthError && (
    error.errorCode === 'monitor_window_timeout' ||
    error.errorCode === 'timed_out' ||
    error.errorCode === 'block_iframe_reload' ||
    error.errorCode === 'popup_window_error' ||
    error.errorCode === 'user_cancelled' ||
    // Safari/ITP hidden-iframe wedges (2026-10-16 field report: iPad stuck on
    // "saved on this iPad only" forever — silent renewal failing fast with
    // these escaped the fatal classification, so nothing ever signed out).
    error.errorCode === 'silent_sso_error' ||
    error.errorCode === 'empty_window_error'
  );
}
